import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { SaveButton } from "components/SaveButton";
import { FormTextFieldSkeleton } from "components/Skeleton";
import { ExtractionStatus, Issue } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { isEqual } from "lodash-es";
import { ReactNode, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Control, FormProvider, useFieldArray, useFormState } from "react-hook-form";
import { useDeepCompareMemoize } from "use-deep-compare-effect";

import {
  AttentionListButton,
  AttentionSection,
  AttentionTargetsProvider,
  PublishTravelTargets,
  travellablePaths,
  countAttention,
  useAcknowledgement,
  useKeepOnTabAway,
  useAttentionTarget,
  useReviewAcknowledgements,
  useSelectOnKeyboardFocus,
} from "../ReviewInteraction";
import { ATTENTION_KINDS, AttentionItem, AttentionKind, listAttention } from "./attention";
import { ExtractionFieldControl } from "./ExtractionFieldControl";
import {
  ExtractionReviewFormData,
  PartialExtractionReviewFormData,
  buildBlankRow,
  buildDefaultValues,
  fieldPath,
  indexConfidence,
  isEmptyReview,
  pairRecordRows,
  rowAnswerKey,
  rowCellKey,
  rowCellPath,
  rowGroupPath,
  rowPath,
  pendingAgainstRecord,
  schemaFields,
  toFormIssues,
  keptFromRecord,
  toRecordDrafts,
  toReviewedValues,
  warnOnUnsupportedKeys,
} from "./formData";
import {
  ExtractionDraftValue,
  ExtractionFieldSpec,
  ExtractionFieldValue,
  ExtractionRecordValues,
  ExtractionRowGroupSpec,
  ExtractionResult,
  ExtractionReviewSchema,
  ExtractionReviewValues,
  extractedBaseline,
  rowConfidenceKey,
  toDraftValue,
} from "./types";

/**
 * Below this confidence a value is flagged for the reviewer's attention.
 * Callers whose extractor is calibrated differently override it.
 */
export const DEFAULT_LOW_CONFIDENCE_THRESHOLD = 0.7;

/**
 * How each reason reads as a heading in the attention list, and the order the
 * reviewer meets them.
 *
 * Validation first, because it is the only one saying a value is wrong rather
 * than worth a look. The rest are ordered by how much is at stake: a row that
 * would disappear, a value that would replace another, a reading nobody has
 * checked.
 */
const ATTENTION_SECTIONS: readonly AttentionSection<AttentionKind>[] = [
  { kind: "issue", title: "Fix before applying" },
  { kind: "recordRow", title: "On the record, not on this document" },
  { kind: "recordConflict", title: "Would replace what the record holds" },
  { kind: "lowConfidence", title: "Worth checking" },
];

/** Only the validator's objections turn the control red; the rest are questions. */
const ATTENTION_ERROR_KINDS: readonly AttentionKind[] = ["issue"];

const FIELD_WIDTHS = { third: 4, half: 6, full: 12 } as const;

/**
 * A multiline field gets the full row; everything else takes its stated width, or
 * half if it does not state one. Always full width on a narrow screen, where
 * tiling would leave the controls too cramped to read the values in.
 */
function fieldGridSize(spec: ExtractionFieldSpec) {
  if (spec.kind === "multiline") return { xs: 12 };
  return { xs: 12, sm: FIELD_WIDTHS[spec.width ?? "half"] };
}

export interface ExtractionReviewGridProps {
  /** The caller's field layout. Nothing about it is interpreted by the grid. */
  schema: ExtractionReviewSchema;
  /** Candidate values from whichever extractor ran. */
  extraction: ExtractionResult | null | undefined;
  /** The source `Document.extractionStatus`. */
  status: ExtractionStatus | null | undefined;
  /**
   * What the record this review will be written over currently holds, keyed the
   * same way as the extraction. Supply it and the grid flags every value about to
   * replace a different one, and surfaces a row the record holds that the reading
   * has no counterpart for instead of dropping it. Omit it and the grid behaves
   * exactly as it did before this existed.
   */
  record?: ExtractionRecordValues;
  /** The source `Document.extractionError`, shown on a failed extraction. */
  extractionError?: string | null;
  /** Receives the reviewed — not the raw — values. */
  onCommit: (reviewed: ExtractionReviewValues) => void | Promise<void>;
  onCancel?: () => void;
  /**
   * Offered from the failed state. Wire this to `useRetryDocumentExtraction`,
   * which re-queues the enrichment job and surfaces its progress in the shared
   * analyzing dialog — this component does not queue anything itself.
   */
  onRetry?: () => void;
  /**
   * The caller's validator, run as the reviewer edits. Issues are keyed in the
   * caller's own namespace — a field key, or `groupKey[index].columnKey` for a
   * row cell, the same paths the extraction pipeline emits — and land on the
   * matching input.
   */
  validator?: (reviewed: ExtractionReviewValues) => Promise<Issue[]>;
  /**
   * Re-runs the validator when it changes, and once when the fields load. For a
   * validator that also reads something outside the grid, e.g. a facility
   * picked beside it.
   */
  revalidateKey?: string | null;
  /** Issues already known before review, keyed the same way. */
  persistedIssues?: Issue[];
  /**
   * Field keys whose issues show inline on the field but are left out of the
   * "to resolve" list and its travel, for checks that warn without asking the
   * reviewer to change anything.
   */
  warningFieldKeys?: readonly string[];
  confirmLabel?: string;
  cancelLabel?: string;
  /** True while the caller's commit is in flight. */
  committing?: boolean;
  /** Disables the confirm button while the caller is missing something the commit needs. */
  commitDisabled?: boolean;
  /**
   * Reports what a close would throw away.
   *
   * `hasWork` is anything the reviewer did — an edit, a value taken, a warning
   * dismissed. `pendingCount` is the stronger signal: values a save would write
   * to the record, which includes the ones the reviewer agreed with by doing
   * nothing at all. A close is worth guarding when either is true, and the
   * warning is worth wording from the second.
   *
   * The grid does not own the close, so it cannot guard it; a caller that can (a
   * dialog with an X and an Escape) uses this to ask before discarding.
   */
  onWorkInProgress?: (state: { hasWork: boolean; pendingCount: number }) => void;
  /** Confidence below which a value is flagged. Defaults to 0.7. */
  lowConfidenceThreshold?: number;
  /** Copy shown above the fields, e.g. what document the values came from. */
  description?: ReactNode;
  /**
   * True while the caller is still fetching the reading — distinct from a
   * `PENDING` status, which says the extractor is working. Keep it false on a
   * background refetch, or a poll replaces a form someone is typing in.
   */
  loading?: boolean;
  /** Passed to `useValidatingForm`; see its `shouldFocusError`. Defaults to true. */
  shouldFocusError?: boolean;
}

/**
 * The shape of the form that is coming. Laid out from the caller's schema with
 * the same column rule the real fields use, so nothing jumps when they land.
 */
function ExtractionReviewSkeleton({ schema }: { schema: ExtractionReviewSchema }) {
  return (
    <Stack spacing={3} aria-busy="true" aria-live="polite">
      <Box component="span" sx={visuallyHidden}>
        Loading the extracted values
      </Box>
      {schema.sections.map((section) => (
        <Stack key={section.key} spacing={1}>
          {section.title && <Skeleton variant="text" width="12ch" />}
          <Grid container columnSpacing={2} rowSpacing={3}>
            {section.fields.map((field) => (
              <Grid key={field.key} size={fieldGridSize(field)}>
                <FormTextFieldSkeleton kind={field.kind === "multiline" ? "long" : "short"} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * The shared Document-AI review/confirm surface: extract -> review -> edit ->
 * commit, for any extractor.
 *
 * The caller supplies a field schema and a commit handler and gets back the
 * values the reviewer confirmed. Every extracted value is rendered as an
 * editable field — including the ones the extractor returned empty, so a
 * reviewer can fill in what it missed — and values the extractor was unsure
 * about are flagged until someone looks at them. Nothing here knows what the
 * values mean, so the waste-profile, bill-of-lading, data-capture, and invoice
 * ingests all reuse it unchanged.
 *
 * Laid out like the document-metadata review it shares components with: labelled
 * small fields in a responsive grid, repeated rows as per-row groups.
 */
export function ExtractionReviewGrid({
  schema,
  extraction,
  record,
  status,
  extractionError,
  onCommit,
  onCancel,
  onRetry,
  validator,
  revalidateKey,
  persistedIssues,
  warningFieldKeys,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  committing = false,
  commitDisabled = false,
  lowConfidenceThreshold = DEFAULT_LOW_CONFIDENCE_THRESHOLD,
  description,
  loading = false,
  onWorkInProgress,
  shouldFocusError = true,
}: ExtractionReviewGridProps) {
  // Ahead of every status check: with no reading there is no status, and the
  // fallthrough below would declare the document empty before we know.
  if (loading && extraction == null) {
    return <ExtractionReviewSkeleton schema={schema} />;
  }

  if (status === ExtractionStatus.Pending) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" aria-live="polite">
        <CircularProgress size={20} />
        <Typography variant="body2">Reading the document…</Typography>
      </Stack>
    );
  }

  if (status === ExtractionStatus.Failed) {
    return (
      <Alert
        severity="error"
        action={
          onRetry && (
            <Button color="inherit" size="small" onClick={onRetry}>
              Try again
            </Button>
          )
        }
      >
        <AlertTitle>We couldn&apos;t read this document</AlertTitle>
        {extractionError ?? "The extraction failed, so there is nothing to review yet."}
      </Alert>
    );
  }

  if (status !== ExtractionStatus.Succeeded || extraction == null) {
    return (
      <Alert severity="info">
        Nothing has been extracted from this document yet, so there is nothing to review.
      </Alert>
    );
  }

  // Mounted only once an extraction has arrived, so the form is seeded from real
  // candidates and a pending-to-succeeded flip populates the fields without the
  // caller having to remount anything itself.
  return (
    <ExtractionReviewForm
      schema={schema}
      extraction={extraction}
      record={record}
      onCommit={onCommit}
      onCancel={onCancel}
      validator={validator}
      revalidateKey={revalidateKey}
      persistedIssues={persistedIssues}
      warningFieldKeys={warningFieldKeys}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      committing={committing}
      commitDisabled={commitDisabled}
      lowConfidenceThreshold={lowConfidenceThreshold}
      description={description}
      onWorkInProgress={onWorkInProgress}
      shouldFocusError={shouldFocusError}
    />
  );
}

type ExtractionReviewFormProps = Pick<
  ExtractionReviewGridProps,
  | "schema"
  | "record"
  | "onCommit"
  | "onCancel"
  | "validator"
  | "revalidateKey"
  | "persistedIssues"
  | "warningFieldKeys"
  | "description"
  | "onWorkInProgress"
  | "commitDisabled"
> & {
  extraction: ExtractionResult;
  shouldFocusError: boolean;
  confirmLabel: string;
  cancelLabel: string;
  committing: boolean;
  lowConfidenceThreshold: number;
};

function ExtractionReviewForm({
  schema,
  extraction,
  record,
  onCommit,
  onCancel,
  validator,
  revalidateKey,
  persistedIssues,
  warningFieldKeys,
  confirmLabel,
  cancelLabel,
  committing,
  commitDisabled,
  lowConfidenceThreshold,
  description,
  onWorkInProgress,
  shouldFocusError,
}: ExtractionReviewFormProps) {
  useEffect(() => warnOnUnsupportedKeys(schema), [schema]);

  const defaultValues = useDeepCompareMemoize(buildDefaultValues(schema, extraction, record));
  // What the extractor itself read, which is only the same as the seed on a
  // first review — see `extractedBaseline`.
  const baseline = useMemo(() => extractedBaseline(extraction), [extraction]);
  const confidence = useMemo(() => indexConfidence(extraction.confidence), [extraction]);
  // Absent for a caller that supplied no record, which is how every field below
  // tells "the record says nothing here" from "there is no record to compare to".
  const recordValues = useMemo(
    () => toRecordDrafts(schemaFields(schema), record?.values),
    [record, schema]
  );
  const fromRecord = useMemo(() => new Set(extraction.fromRecord ?? []), [extraction]);

  const formValidator = useCallback(
    async (data: ExtractionReviewFormData): Promise<Issue[]> => {
      if (!validator) return [];
      return toFormIssues(schema, await validator(toReviewedValues(schema, data)));
    },
    [schema, validator]
  );

  const memoizedPersistedIssues = useDeepCompareMemoize(
    toFormIssues(schema, persistedIssues ?? [])
  );

  const form = useValidatingForm<ExtractionReviewFormData>(
    defaultValues,
    memoizedPersistedIssues,
    formValidator,
    // The grid has no `stateFields`-style dynamic key indirection; issue keys are
    // already form paths by the time they get here.
    [],
    // useValidatingForm resets whenever the seed data changes. Without this, a
    // re-delivered extraction (a background refetch, a re-run) would silently
    // discard corrections the reviewer had already typed.
    { keepDirtyValuesOnReset: true, shouldFocusError }
  );
  const { control, handleSubmit, issues, watch, getValues, trigger } = form;
  const hasValidator = validator != null;
  useEffect(() => {
    if (hasValidator && revalidateKey !== undefined) void trigger();
  }, [hasValidator, revalidateKey, trigger]);
  const { isSubmitting } = useFormState({ control });
  const busy = committing || isSubmitting;

  const { acknowledged, acknowledge, acknowledgeAll } = useReviewAcknowledgements();

  // Subscribed through the callback form rather than `useWatch`, which re-renders
  // this component on every keystroke to recompute a summary that rarely changes.
  // The walk runs here instead, off the render path, and state moves only when its
  // answer actually differs — which is a handful of times in a session, not once
  // per character.
  const countedIssues = useMemo(() => {
    const warningPaths = new Set<string>((warningFieldKeys ?? []).map((key) => fieldPath(key)));
    return issues.filter((issue) => !warningPaths.has(issue.key ?? ""));
  }, [issues, warningFieldKeys]);

  const readSummary = useCallback(
    (data: PartialExtractionReviewFormData): ReviewSummary => ({
      attention: listAttention({
        schema,
        extraction,
        record,
        data,
        confidence,
        lowConfidenceThreshold,
        issues: countedIssues,
        acknowledged,
      }),
      isEmpty: isEmptyReview(data),
      // Compared against the seed rather than read from `isDirty`, for the reason
      // `isReviewed` avoids `dirtyFields`: this form is reset whenever the reading
      // is re-delivered, which clears RHF's dirty state while the reviewer's edits
      // survive under `keepDirtyValuesOnReset`. An acknowledgement leaves no trace
      // in the form at all, so it counts separately.
      hasWork: acknowledged.size > 0 || !isEqual(data, defaultValues),
      pending: pendingAgainstRecord(schemaFields(schema), data, recordValues),
    }),
    [
      acknowledged,
      confidence,
      countedIssues,
      defaultValues,
      extraction,
      lowConfidenceThreshold,
      recordValues,
      record,
      schema,
    ]
  );

  const [summary, setSummary] = useState<ReviewSummary>(() => readSummary(getValues()));
  useEffect(() => {
    setSummary((previous) => {
      const next = readSummary(getValues());
      // Compared whole rather than field by field: this runs off the render path,
      // where the cost of a deep compare is irrelevant, and a hand-rolled one
      // stops noticing anything added to the summary later.
      return isEqual(previous, next) ? previous : next;
    });
    const subscription = watch((data) =>
      setSummary((previous) => {
        const next = readSummary(data as PartialExtractionReviewFormData);
        return isEqual(previous, next) ? previous : next;
      })
    );
    return () => subscription.unsubscribe();
  }, [getValues, readSummary, watch]);

  const { attention, isEmpty, hasWork, pending } = summary;
  const pendingCount = pending.length;
  useEffect(
    () => onWorkInProgress?.({ hasWork, pendingCount }),
    [hasWork, pendingCount, onWorkInProgress]
  );

  const handleConfirm = useCallback(
    () =>
      handleSubmit((data) =>
        onCommit({
          ...toReviewedValues(schema, data),
          keptFromRecord: keptFromRecord(schemaFields(schema), data, fromRecord, recordValues),
        })
      )(),
    [handleSubmit, onCommit, schema, fromRecord, recordValues]
  );

  const selectOnFocus = useSelectOnKeyboardFocus();
  // Computed once here rather than inside each control that needs it: the jump
  // button hides on it, the publisher hands it to the fields, and both must mean
  // the same order.
  const travelPaths = useMemo(() => travellablePaths(attention), [attention]);
  /**
   * What "keep all" answers: the flags, and not the validation errors.
   *
   * A low score or a disagreement is a question the reviewer is entitled to
   * answer with "I looked, it is fine". A validation error is not — the value is
   * wrong and dismissing it would hide a reason the caller will refuse the save
   * for anyway.
   */
  const keepAllKeys = useMemo(
    () => attention.flatMap((item) => (item.answerKey == null ? [] : [item.answerKey])),
    [attention]
  );

  /**
   * Which key answers the field at a path, for tabbing off it.
   *
   * Built from the walk rather than the path itself: a repeated row's cell is
   * answered under a key tied to the row the extractor read, so the two differ
   * exactly where getting it wrong would answer a different row's question.
   */
  const answerKeys = useMemo(() => {
    const byPath = new Map<string, string>();
    for (const item of attention) {
      if (item.answerKey != null && !byPath.has(item.path)) byPath.set(item.path, item.answerKey);
    }
    return byPath;
  }, [attention]);

  const keepOnTab = useKeepOnTabAway({
    answerKeyForPath: useCallback((path: string) => answerKeys.get(path), [answerKeys]),
    acknowledge,
  });

  return (
    <FormProvider {...form}>
      <AttentionTargetsProvider acknowledged={acknowledged} acknowledge={acknowledge}>
        <PublishTravelTargets paths={travelPaths} />
        {/* Tabbing into a field selects what it holds, so a reviewer correcting a
            poor reading types over it instead of clearing it first. The same
            behaviour the manifest ingest form has, from the same hook — the two
            surfaces do the same job and a reviewer moving between them should not
            have to relearn how typing works. */}
        {/* And tabbing back off a flagged field answers it, so a reviewer reading
            down the form does not have to press a chip to say "this is fine" on
            every field that has something to say. */}
        <Stack spacing={3} {...selectOnFocus} {...keepOnTab}>
          {description && <Typography variant="body2">{description}</Typography>}

          <ReviewAttentionBanner attention={attention} />

          {schema.sections.map((section) => (
            <Section
              key={section.key}
              section={section}
              baseline={baseline}
              fromRecord={fromRecord}
              confidence={confidence}
              recordValues={recordValues}
              lowConfidenceThreshold={lowConfidenceThreshold}
              control={control}
              disabled={busy}
            />
          ))}

          {(schema.rowGroups ?? []).map((group) => (
            <RowGroup
              key={group.key}
              group={group}
              extraction={extraction}
              record={record}
              confidence={confidence}
              lowConfidenceThreshold={lowConfidenceThreshold}
              control={control}
              disabled={busy}
            />
          ))}

          <ReviewFooter
            attention={attention}
            travelPaths={travelPaths}
            keepAllKeys={keepAllKeys}
            onKeepAll={acknowledgeAll}
            onCancel={onCancel}
            cancelLabel={cancelLabel}
            confirmLabel={confirmLabel}
            busy={busy}
            isEmpty={isEmpty}
            commitDisabled={commitDisabled}
            onConfirm={handleConfirm}
          />
        </Stack>
      </AttentionTargetsProvider>
    </FormProvider>
  );
}

/**
 * The confirm row: travel, the list of what wants attention, then cancel and
 * confirm. Its own component because travelling to a field reads the target
 * registry, which only exists inside the provider.
 */
function ReviewFooter({
  attention,
  travelPaths,
  keepAllKeys,
  onKeepAll,
  onCancel,
  cancelLabel,
  confirmLabel,
  busy,
  isEmpty,
  commitDisabled,
  onConfirm,
}: {
  attention: readonly AttentionItem[];
  /** The same travel order the fields advance along, so the two cannot disagree. */
  travelPaths: readonly string[];
  /**
   * The keys a single press can answer; empty when there are none.
   *
   * Keys rather than paths: a repeated row's cell is answered under a key tied to
   * the row the extractor read, so the answer survives a row above it being
   * removed. A validation error carries no key, which is how it stays standing.
   */
  keepAllKeys: readonly string[];
  onKeepAll: (paths: readonly string[]) => void;
  onCancel?: () => void;
  cancelLabel: string;
  confirmLabel: string;
  busy: boolean;
  isEmpty: boolean;
  commitDisabled?: boolean;
  onConfirm: () => void;
}) {
  return (
    // Pinned to the bottom of the scrolling review column, the way
    // `FloatingSaveBar` pins the app's other save rows. Everything here has to be
    // reachable from anywhere in the form, and travel most of all: it exists for a
    // form long enough to hide its own flagged values, so a control that scrolls
    // away with the banner is missing exactly when it is wanted.
    <Stack
      direction="row"
      spacing={2}
      justifyContent="flex-end"
      alignItems="center"
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        py: 1.5,
      }}
    >
      <AttentionListButton
        attention={attention}
        sections={ATTENTION_SECTIONS}
        errorKinds={ATTENTION_ERROR_KINDS}
      />
      {keepAllKeys.length > 0 && (
        // Skippability. Reviewing every flag is the careful path and has to stay
        // easy; being made to walk it when the document is plainly fine is what
        // makes a queue feel like a ritual. Named for what it does to the values —
        // nothing — rather than "dismiss all", which sounds like discarding them.
        <Button onClick={() => onKeepAll(keepAllKeys)} disabled={busy} size="small">
          Keep all {keepAllKeys.length}
        </Button>
      )}
      {onCancel && (
        <Button onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
      )}
      <SaveButton
        saveText={confirmLabel}
        hideStartIcon
        loading={busy}
        loadingText={`${confirmLabel}…`}
        // An extraction that found nothing must never be committed as if it
        // had; the reviewer fills something in first.
        disabled={isEmpty || commitDisabled}
        onClick={onConfirm}
      />
    </Stack>
  );
}

/** What the summary controls read: the walk, and the two whole-form facts. */
interface ReviewSummary {
  attention: AttentionItem[];
  isEmpty: boolean;
  hasWork: boolean;
  /**
   * Field keys a save would change on the record.
   *
   * The count the reviewer is actually working down, and the one that makes
   * closing dangerous: these are values the review would write and a close would
   * throw away, whether or not anybody touched them.
   */
  pending: string[];
}

/** "1 value needs" / "3 values need", so each banner line reads as a sentence. */
function valuesPhrase(count: number, singularVerb: string, pluralVerb: string): string {
  return count === 1 ? `1 value ${singularVerb}` : `${count} values ${pluralVerb}`;
}

/**
 * What still wants the reviewer's attention: values showing an untouched
 * low-confidence extraction, values about to replace something the record holds,
 * and rows the record holds that this reading does not.
 *
 * Counted from `listAttention`, the same walk the lists and "jump to next" read,
 * so the summary and what travel visits cannot disagree. One banner rather than
 * three: stacked warnings cost each other their meaning, which is the reasoning
 * the bill-of-lading review's own intro already records.
 *
 * Validation issues are deliberately not a line here — they are errors, and
 * counting them into a warning banner would understate them; the red issue list
 * is theirs. Travel still visits them, because the reviewer's question is "where
 * do I have to look", not "which check objected".
 */
function ReviewAttentionBanner({ attention }: { attention: readonly AttentionItem[] }) {
  const counts = useMemo(() => countAttention(attention, ATTENTION_KINDS), [attention]);

  const lines = [
    counts.lowConfidence > 0
      ? `${valuesPhrase(counts.lowConfidence, "needs", "need")} review before you confirm.`
      : null,
    counts.recordConflict > 0
      ? `${valuesPhrase(counts.recordConflict, "would", "would")} replace something already on the record.`
      : null,
    counts.recordRow > 0
      ? `${counts.recordRow === 1 ? "1 row is" : `${counts.recordRow} rows are`} on the record but not on this document — keep or remove ${counts.recordRow === 1 ? "it" : "them"}.`
      : null,
  ].filter((line): line is string => line != null);

  if (lines.length === 0) return null;

  return (
    <Alert severity="warning" aria-live="polite">
      <Stack spacing={0.5}>
        {lines.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </Stack>
    </Alert>
  );
}

/**
 * One titled block of single-value fields, tiled by the schema's own widths.
 *
 * Memoized with the two row components below. Every prop here is stable for the
 * life of the reading, so once the parent stops handing them new values a
 * keystroke re-renders the banner and the counts and stops at these boundaries.
 * Without that the field controls' own memoization gains nothing: their wrappers
 * re-render and rebuild the MUI tree around them regardless.
 */
const Section = memo(function Section({
  section,
  baseline,
  fromRecord,
  confidence,
  recordValues,
  lowConfidenceThreshold,
  control,
  disabled,
}: {
  section: ExtractionReviewSchema["sections"][number];
  baseline: ReturnType<typeof extractedBaseline>;
  fromRecord: ReadonlySet<string>;
  confidence: Map<string, number>;
  recordValues: Map<string, ExtractionDraftValue>;
  lowConfidenceThreshold: number;
  control: Control<ExtractionReviewFormData>;
  disabled: boolean;
}) {
  return (
    <Box>
      {section.title && (
        <Typography variant="subtitle2" mb={1.5}>
          {section.title}
        </Typography>
      )}
      <Grid container columnSpacing={2} rowSpacing={3}>
        {section.fields.map((field) => (
          <Grid key={field.key} size={fieldGridSize(field)}>
            <ExtractionFieldControl
              spec={field}
              control={control}
              path={fieldPath(field.key)}
              extracted={toDraftValue(field, baseline.values?.[field.key])}
              hasCandidate
              confidence={confidence.get(field.key) ?? null}
              acknowledgeKey={field.key}
              recordValue={recordValues.get(field.key)}
              fromRecord={fromRecord.has(field.key)}
              lowConfidenceThreshold={lowConfidenceThreshold}
              label={field.label}
              disabled={disabled}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

/**
 * What the record holds in one cell of a paired row, or `undefined` when there is
 * no paired record row — the same distinction the scalar fields draw between a
 * record that is silent and no record at all.
 */
function recordCellValue(
  column: ExtractionFieldSpec,
  recordRow: Readonly<Record<string, ExtractionFieldValue>> | undefined
): ExtractionDraftValue | undefined {
  if (!recordRow) return undefined;
  return toDraftValue(column, recordRow[column.key]);
}

const RowGroup = memo(function RowGroup({
  group,
  extraction,
  record,
  confidence,
  lowConfidenceThreshold,
  control,
  disabled,
}: {
  group: ExtractionRowGroupSpec;
  extraction: ExtractionResult;
  record: ExtractionRecordValues | undefined;
  confidence: Map<string, number>;
  lowConfidenceThreshold: number;
  control: Control<ExtractionReviewFormData>;
  disabled: boolean;
}) {
  const { fields, append, remove } = useFieldArray({ control, name: rowGroupPath(group.key) });
  const columnKeys = useMemo(() => group.columns.map((column) => column.key), [group]);
  const baseline = useMemo(() => extractedBaseline(extraction), [extraction]);
  // Which record row each extracted row stands for, so a cell can say what it is
  // about to replace. Keyed by extracted index rather than by the row's position
  // here, for the same reason confidence is: the list renumbers as rows go. Paired
  // against the seed, not the extractor's own reading — the seed is what the grid
  // shows, and a correction saved earlier can have renamed a row since.
  const recordRows = useMemo(
    () => pairRecordRows(group, extraction.rows?.[group.key], record?.rows?.[group.key]),
    [extraction, group, record]
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle2">{group.title}</Typography>
        <Button
          size="small"
          startIcon={<Add />}
          disabled={disabled}
          onClick={() => append(buildBlankRow(columnKeys))}
        >
          {`Add ${group.rowNoun}`}
        </Button>
      </Stack>

      {fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {`No ${group.rowNoun} rows were extracted.`}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {fields.map((row, index) => (
            <RepeatedRow
              key={row.id}
              group={group}
              index={index}
              extractedIndex={row.extractedIndex}
              fromRecordOnly={row.origin === "record"}
              answerKey={row.origin === "record" ? rowAnswerKey(group, fields, index) : null}
              extractedRow={
                row.extractedIndex == null
                  ? undefined
                  : baseline.rows?.[group.key]?.[row.extractedIndex]
              }
              recordRow={
                row.extractedIndex == null
                  ? undefined
                  : recordRows.byExtractedIndex.get(row.extractedIndex)
              }
              confidence={confidence}
              lowConfidenceThreshold={lowConfidenceThreshold}
              control={control}
              disabled={disabled}
              onRemove={remove}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
});

/** One row of a repeated group: its own named group, its cells, and its controls. */
const RepeatedRow = memo(function RepeatedRow({
  group,
  index,
  extractedIndex,
  fromRecordOnly,
  answerKey,
  extractedRow,
  recordRow,
  confidence,
  lowConfidenceThreshold,
  control,
  disabled,
  onRemove,
}: {
  group: ExtractionRowGroupSpec;
  index: number;
  extractedIndex: number | null;
  /** True when the record holds this row and the reading has no counterpart. */
  fromRecordOnly: boolean;
  /** The key an answer about this whole row is kept under, when it can have one. */
  answerKey: string | null;
  extractedRow: Readonly<Record<string, ExtractionFieldValue>> | undefined;
  recordRow: Readonly<Record<string, ExtractionFieldValue>> | undefined;
  confidence: Map<string, number>;
  lowConfidenceThreshold: number;
  control: Control<ExtractionReviewFormData>;
  disabled: boolean;
  onRemove: (index: number) => void;
}) {
  const rowLabel = `${group.rowNoun} ${index + 1}`;
  // A question about a row's existence has no cell to answer it, so travel lands
  // on the row's own box rather than inside it.
  const targetRef = useAttentionTarget(rowPath(group.key, index), "self");
  // Keeping the row is the answer, and it is remembered by the row's identity
  // rather than its position — the row moves when one above it is removed.
  const { isAcknowledged: rowAnswered, acknowledge: answerRow } = useAcknowledgement(
    fromRecordOnly ? answerKey : null
  );

  return (
    // Named group so a repeated column stays addressable per row without
    // repeating the row in every visible field label.
    <Box
      ref={targetRef}
      role="group"
      aria-label={rowLabel}
      sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="body2" color="text.secondary">
          {rowLabel.charAt(0).toUpperCase() + rowLabel.slice(1)}
        </Typography>
        <IconButton
          size="small"
          disabled={disabled}
          aria-label={`Remove ${rowLabel}`}
          title={`Remove ${rowLabel}`}
          onClick={() => onRemove(index)}
        >
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      {/* A row nobody would otherwise know was at stake: the record holds it,
          this document does not mention it, and confirming writes the list as it
          stands. Whether that is a hand-added row or one the document has since
          dropped is the reviewer's call — the grid cannot tell, which is why it
          asks. */}
      {fromRecordOnly && !rowAnswered && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={answerRow}>
              Dismiss warning
            </Button>
          }
        >
          {`This ${group.rowNoun} is on the record, but this document does not list it. Keep it or remove it.`}
        </Alert>
      )}

      <Grid container columnSpacing={2} rowSpacing={3}>
        {group.columns.map((column) => (
          <Grid key={column.key} size={fieldGridSize(column)}>
            <ExtractionFieldControl
              spec={column}
              control={control}
              path={rowCellPath(group.key, index, column.key)}
              extracted={toDraftValue(column, extractedRow?.[column.key])}
              hasCandidate={extractedIndex != null}
              confidence={
                extractedIndex == null
                  ? null
                  : (confidence.get(rowConfidenceKey(group.key, extractedIndex, column.key)) ??
                    null)
              }
              acknowledgeKey={
                extractedIndex == null ? null : rowCellKey(group.key, extractedIndex, column.key)
              }
              recordValue={recordCellValue(column, recordRow)}
              lowConfidenceThreshold={lowConfidenceThreshold}
              label={column.label}
              disabled={disabled}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});
